'use client';

import React, { useEffect } from 'react';
import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import {
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/20/solid';

interface AlertProps {
  type: 'success' | 'error' | 'warning';
  message: string;
  onDismiss?: () => void;
  autoDismiss?: boolean; // Auto-dismiss after 3 seconds (default: true for success, false for error/warning)
  autoDismissDelay?: number; // Delay in milliseconds (default: 3000)
}

export default function Alert({
  type,
  message,
  onDismiss,
  autoDismiss,
  autoDismissDelay = 3000,
}: AlertProps) {
  const { darkMode } = useDarkMode();

  // Auto-dismiss logic
  useEffect(() => {
    // Default: auto-dismiss success messages, keep error/warning messages
    const shouldAutoDismiss = autoDismiss !== undefined ? autoDismiss : type === 'success';

    if (shouldAutoDismiss && onDismiss) {
      const timer = setTimeout(() => {
        onDismiss();
      }, autoDismissDelay);

      return () => clearTimeout(timer);
    }
  }, [type, onDismiss, autoDismiss, autoDismissDelay]);

  return (
    <div
      className={`rounded-md p-4 ${
        type === 'success'
          ? darkMode
            ? 'bg-green-500/10'
            : 'bg-green-50'
          : type === 'warning'
            ? darkMode
              ? 'bg-yellow-500/10'
              : 'bg-yellow-50'
            : darkMode
              ? 'bg-red-500/10'
              : 'bg-red-50'
      }`}
    >
      <div className="flex">
        <div className="shrink-0">
          {type === 'success' ? (
            <CheckCircleIcon
              aria-hidden="true"
              className={`size-5 ${darkMode ? 'text-green-400' : 'text-green-400'}`}
            />
          ) : type === 'warning' ? (
            <ExclamationTriangleIcon
              aria-hidden="true"
              className={`size-5 ${darkMode ? 'text-yellow-400' : 'text-yellow-400'}`}
            />
          ) : (
            <XCircleIcon
              aria-hidden="true"
              className={`size-5 ${darkMode ? 'text-red-400' : 'text-red-400'}`}
            />
          )}
        </div>
        <div className="ml-3">
          <p
            className={`text-sm font-medium ${
              type === 'success'
                ? darkMode
                  ? 'text-green-400'
                  : 'text-green-800'
                : type === 'warning'
                  ? darkMode
                    ? 'text-yellow-400'
                    : 'text-yellow-800'
                  : darkMode
                    ? 'text-red-400'
                    : 'text-red-800'
            }`}
          >
            {message}
          </p>
        </div>
        {onDismiss && (
          <div className="ml-auto pl-3">
            <div className="-mx-1.5 -my-1.5">
              <button
                type="button"
                onClick={onDismiss}
                className={`inline-flex rounded-md p-1.5 focus:ring-2 focus:ring-offset-2 focus:outline-hidden ${
                  type === 'success'
                    ? darkMode
                      ? 'bg-green-500/10 text-green-400 hover:bg-green-500/20 focus:ring-green-600 focus:ring-offset-gray-900'
                      : 'bg-green-50 text-green-500 hover:bg-green-100 focus:ring-green-600 focus:ring-offset-green-50'
                    : type === 'warning'
                      ? darkMode
                        ? 'bg-yellow-500/10 text-yellow-400 hover:bg-yellow-500/20 focus:ring-yellow-600 focus:ring-offset-gray-900'
                        : 'bg-yellow-50 text-yellow-500 hover:bg-yellow-100 focus:ring-yellow-600 focus:ring-offset-yellow-50'
                      : darkMode
                        ? 'bg-red-500/10 text-red-400 hover:bg-red-500/20 focus:ring-red-600 focus:ring-offset-gray-900'
                        : 'bg-red-50 text-red-500 hover:bg-red-100 focus:ring-red-600 focus:ring-offset-red-50'
                }`}
              >
                <span className="sr-only">Dismiss</span>
                <XMarkIcon aria-hidden="true" className="size-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
